/**
 * CommunityLayout — the logged-in shell for VOLUNTEER_COMMUNITY users
 * (`/community-dashboard/*`). A focused, task-oriented community workspace:
 * People + Knowledge + Awareness + Collaboration + Action. Deliberately NOT the
 * generic social shell (no Groups/Reels/Competitions/Messages chrome).
 *
 * Structure mirrors `GovLayout` (sticky header, left sidebar, main, right rail,
 * mobile bottom nav, mobile drawer) but consumes `communityNav.ts` and the
 * `CommunityContext` (community + role). Re-exports the `Gov*` helpers so pages
 * import everything from one place.
 */
import { memo, useState, useRef, type ReactNode, type ComponentType } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Menu, Search, Bell, LogOut, User as UserIcon, Bot, X,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'
import { LanguageSwitcher } from '@/components/portal/LanguageSwitcher'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { useModalA11y } from '@/components/portal/useModalA11y'
import { RightRail } from '@/components/layout/RightRail'
// Re-export the Gov helpers for one-stop importing by community pages.
export { GovLayout } from '@/components/layout/GovLayout'
export { GovCard, GovPageTitle, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { GovCard } from '@/components/layout/GovLayout'
import {
  COMMUNITY_SIDEBAR_SECTIONS, COMMUNITY_MOBILE_NAV, resolveCommunityPath,
  type CommunityNavItem, type CommunityNavSection,
} from '@/config/communityNav'
import { useCommunity } from './CommunityContext'

// ──────────────────────────────────────────────────────────────────────────
// Active-route helper — dashboard is prefix-collision-safe (exact match only).
// ──────────────────────────────────────────────────────────────────────────
function useIsActive() {
  const { pathname } = useLocation()
  return (path: string) => {
    if (pathname === path) return true
    if (path === '/community-dashboard') return false
    return pathname.startsWith(path + '/')
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Header
// ──────────────────────────────────────────────────────────────────────────
interface HeaderProps { onMenuClick: () => void }
const CommunityHeader = memo(function CommunityHeader({ onMenuClick }: HeaderProps) {
  const { t } = useTranslation()
  const { user, logout } = useAuthStore()
  const { community } = useCommunity()
  const navigate = useNavigate()

  const onLogout = () => { logout(); navigate('/login') }

  return (
    <header className="sticky top-0 z-40 h-14 bg-white/95 backdrop-blur border-b border-gray-100 flex items-center px-3 sm:px-4 gap-2">
      <button
        type="button"
        onClick={onMenuClick}
        className="lg:hidden p-2 -ml-1 rounded-lg text-gray-600 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label={t('nav.openMenu')}
      >
        <Menu className="w-5 h-5" />
      </button>

      <Link to="/community-dashboard" className="flex items-center gap-2 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shrink-0">
          <UserIcon className="w-5 h-5 text-white" aria-hidden="true" />
        </div>
        <div className="min-w-0 hidden sm:block">
          <p className="text-sm font-semibold text-gray-900 truncate leading-tight">
            {community?.name ?? t('nav.community.brand')}
          </p>
          <p className="text-[11px] text-gray-400 truncate leading-tight">{t('nav.community.brandSub')}</p>
        </div>
      </Link>

      <div className="ml-auto flex items-center gap-1">
        <Link to="/search" aria-label={t('nav.search')}
          className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
          <Search className="w-5 h-5" />
        </Link>
        <LanguageSwitcher variant="compact" />
        <ThemeToggle variant="icon" size="md" />
        <Link to="/notifications" aria-label={t('nav.notifications')}
          className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center relative">
          <Bell className="w-5 h-5" />
        </Link>
        <Link to="/assistant" aria-label={t('nav.assistant')}
          className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 min-h-[44px] min-w-[44px] hidden sm:flex items-center justify-center">
          <Bot className="w-5 h-5" />
        </Link>
        <Link to={`/profile/${user?.username ?? ''}`} aria-label={t('nav.profile')}
          className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
          <UserIcon className="w-5 h-5" />
        </Link>
        <button type="button" onClick={onLogout} aria-label={t('nav.logout')}
          className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
          <LogOut className="w-5 h-5" />
        </button>
      </div>
    </header>
  )
})

// ──────────────────────────────────────────────────────────────────────────
// Sidebar (shared by desktop aside + mobile drawer)
// ──────────────────────────────────────────────────────────────────────────
interface SidebarProps { onNavigate?: () => void }
const CommunitySidebar = memo(function CommunitySidebar({ onNavigate }: SidebarProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { canManage } = useCommunity()
  const isActive = useIsActive()

  const renderSection = (section: CommunityNavSection) => {
    const items = section.items.filter((it) => !it.leaderOnly || canManage)
    if (items.length === 0) return null
    return (
      <div
        key={section.titleKey ?? 'main'}
        className={cn('flex flex-col gap-0.5', section.bottom && 'mt-auto pt-3 border-t border-gray-100')}
      >
        {section.titleKey && (
          <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {t(section.titleKey)}
          </p>
        )}
        {items.map((item: CommunityNavItem) => {
          const path = resolveCommunityPath(item, user?.username)
          const Icon = item.icon
          const active = isActive(path)
          return (
            <Link
              key={path}
              to={path}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[40px]',
                active ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
              )}
            >
              <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
              <span className="truncate">{t(`nav.community.${item.key}`, item.label)}</span>
            </Link>
          )
        })}
      </div>
    )
  }

  return (
    <nav aria-label="Community" className="flex flex-col h-full gap-1 px-3 py-3 overflow-y-auto">
      {COMMUNITY_SIDEBAR_SECTIONS.map(renderSection)}
    </nav>
  )
})

// ──────────────────────────────────────────────────────────────────────────
// Mobile bottom nav (5 items, thumb-reachable)
// ──────────────────────────────────────────────────────────────────────────
const CommunityBottomNav = memo(function CommunityBottomNav() {
  const { t } = useTranslation()
  const isActive = useIsActive()
  return (
    <nav aria-label="Community mobile" className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-100 pb-[env(safe-area-inset-bottom)]">
      <ul className="w-full grid grid-cols-5">
        {COMMUNITY_MOBILE_NAV.map((item) => {
          const Icon = item.icon
          const active = isActive(item.path)
          return (
            <li key={item.path} className="min-w-0">
              <Link
                to={item.path}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-medium transition-colors',
                  active ? 'text-blue-600' : 'text-gray-500 hover:text-gray-800',
                )}
              >
                <Icon className="w-5 h-5" aria-hidden="true" />
                <span className="truncate max-w-full px-0.5">{t(`nav.community.${item.key}`, item.label)}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
})

// ──────────────────────────────────────────────────────────────────────────
// Mobile drawer (focus-trapped)
// ──────────────────────────────────────────────────────────────────────────
interface DrawerProps { open: boolean; onClose: () => void }
const CommunityMobileDrawer = memo(function CommunityMobileDrawer({ open, onClose }: DrawerProps) {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement>(null)
  useModalA11y(open, onClose, panelRef)
  if (!open) return null
  return (
    <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={t('nav.community.drawerLabel')}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div ref={panelRef} className="absolute left-0 top-0 h-full w-72 max-w-[80%] bg-white shadow-xl flex flex-col">
        <div className="h-14 flex items-center justify-between px-3 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-900">{t('nav.community.brand')}</span>
          <button type="button" onClick={onClose} aria-label={t('nav.closeMenu')}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <CommunitySidebar onNavigate={onClose} />
        </div>
      </div>
    </div>
  )
})

// ──────────────────────────────────────────────────────────────────────────
// Layout body
// ──────────────────────────────────────────────────────────────────────────
interface CommunityLayoutProps { children: ReactNode }

function CommunityLayoutInner({ children }: CommunityLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { loading, error } = useCommunity()
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-gray-50">
      <CommunityHeader onMenuClick={() => setDrawerOpen(true)} />
      <div className="lg:flex">
        <aside className="hidden lg:block w-64 shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] border-r border-gray-100 bg-white overflow-y-auto">
          <CommunitySidebar />
        </aside>
        <main className="flex-1 min-w-0">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 pb-24 lg:pb-6">
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : error ? (
              <GovCard>
                <div className="py-8 text-center">
                  <p className="text-5xl mb-2">🤝</p>
                  <h2 className="text-lg font-semibold text-gray-900">{t('community.loadErrorTitle')}</h2>
                  <p className="text-sm text-gray-500 mt-1">{error}</p>
                </div>
              </GovCard>
            ) : (
              children
            )}
          </div>
        </main>
        <RightRail />
      </div>
      <CommunityBottomNav />
      <CommunityMobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  )
}

/** Public layout. The CommunityProvider is mounted once at the route level
 *  (App.tsx) so pages can call useCommunity() in their own bodies; this layout
 *  only renders the shell chrome + children. */
export function CommunityLayout({ children }: CommunityLayoutProps) {
  return <CommunityLayoutInner>{children}</CommunityLayoutInner>
}

export default CommunityLayout

// Keep the ComponentType import referenced for downstream consumers.
export type { ComponentType }